import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Per-channel FX state — `activeEffects` is which effects are flipped ON
 * (e.g. `{0: {comp: true, reverb: false}}`); `fxParamsByChannel` mirrors
 * `engine.fxParams[ch]` so knob movements feel immediate AND memo'd children
 * actually re-render when knobs move.
 *
 * The engine remains the source of truth for audio; this hook keeps a React-
 * shaped projection of the engine's state and exposes idempotent handlers
 * that update both halves in lockstep.
 *
 * Returns the standard FX surface PLUS the iter-32 automation extensions:
 *  - automationArmedByChannel    Map<idx, bool>      — which channels record knob moves
 *  - automationSummaries         Map<idx, {totalPoints, params:[{name,key,count}]}>
 *  - toggleAutomationArm(idx)
 *  - clearChannelAutomation(idx)
 *
 * The summaries refresh on a polling cadence (60ms) when playing so the UI
 * point-count stays roughly live without each `setChannelEffectParam` call
 * having to thread through React state — keeps the high-frequency knob-drag
 * path off the render critical loop.
 */
export default function useFxState(engine) {
  const channelCount = engine.channels.length;

  const [activeEffects, setActiveEffects] = useState({});
  const [automationArmedByChannel, setAutomationArmedByChannel] = useState({});
  const [automationSummaries, setAutomationSummaries] = useState({});

  const [fxParamsByChannel, setFxParamsByChannel] = useState(() => {
    const init = {};
    engine.channels.forEach((c, i) => { init[i] = engine.getChannelFxParams(i); });
    return init;
  });

  const toggleEffectOnChannel = useCallback((channelIdx, effectKey) => {
    setActiveEffects((prev) => {
      const currentVal = !!(prev[channelIdx]?.[effectKey]);
      const nextVal = !currentVal;
      engine.setChannelEffect(channelIdx, effectKey, nextVal);
      return {
        ...prev,
        [channelIdx]: { ...(prev[channelIdx] || {}), [effectKey]: nextVal },
      };
    });
  }, [engine]);

  const handleFxParamChange = useCallback((channelIdx, effectKey, paramKey, value) => {
    engine.setChannelEffectParam(channelIdx, effectKey, paramKey, value);
    setFxParamsByChannel((prev) => ({
      ...prev,
      [channelIdx]: {
        ...(prev[channelIdx] || {}),
        [effectKey]: { ...((prev[channelIdx] || {})[effectKey] || {}), [paramKey]: value },
      },
    }));
  }, [engine]);

  const handleLoadFxPreset = useCallback((channelIdx, preset) => {
    if (!preset?.params) return;
    for (const [effectKey, group] of Object.entries(preset.params)) {
      for (const [paramKey, value] of Object.entries(group)) {
        engine.setChannelEffectParam(channelIdx, effectKey, paramKey, value);
      }
    }
    setFxParamsByChannel((prev) => ({
      ...prev,
      [channelIdx]: structuredClone(preset.params),
    }));
  }, [engine]);

  const fxCounts = useMemo(() => {
    const out = new Array(channelCount).fill(0);
    Object.entries(activeEffects).forEach(([k, v]) => {
      out[Number(k)] = Object.values(v || {}).filter(Boolean).length;
    });
    return out;
  }, [activeEffects, channelCount]);

  /* ─── Automation API ────────────────────────────────────────────────── */
  const toggleAutomationArm = useCallback((channelIdx) => {
    setAutomationArmedByChannel((prev) => {
      const next = !prev[channelIdx];
      engine.setChannelAutomationArm(channelIdx, next);
      return { ...prev, [channelIdx]: next };
    });
  }, [engine]);

  const clearChannelAutomation = useCallback((channelIdx, name, key) => {
    engine.clearChannelAutomation(channelIdx, name, key);
    // Refresh the summary so the UI badge / count clears immediately.
    setAutomationSummaries((prev) => ({
      ...prev,
      [channelIdx]: engine.getChannelAutomationSummary(channelIdx),
    }));
  }, [engine]);

  // Light polling of summaries — only the LIVE-armed channels get refreshed
  // while playing so we don't burn idle CPU. 200ms cadence matches the
  // meter-update interval and is plenty for human-perceptible point counts.
  useEffect(() => {
    const id = setInterval(() => {
      if (!engine.playing) return;
      let next = null;
      Object.entries(automationArmedByChannel).forEach(([k, armed]) => {
        if (!armed) return;
        const idx = Number(k);
        const summary = engine.getChannelAutomationSummary(idx);
        if (!next) next = {};
        next[idx] = summary;
      });
      if (next) setAutomationSummaries((prev) => ({ ...prev, ...next }));
    }, 200);
    return () => clearInterval(id);
  }, [engine, automationArmedByChannel]);

  return {
    activeEffects, setActiveEffects,
    fxParamsByChannel,
    fxCounts,
    toggleEffectOnChannel,
    handleFxParamChange,
    handleLoadFxPreset,
    // automation extensions
    automationArmedByChannel,
    automationSummaries,
    toggleAutomationArm,
    clearChannelAutomation,
  };
}
